/**
 * Extracts a sortable date from a rendered CSL citation string.
 *
 * The IRF middleware only delivers the fully formatted `citation_entry` HTML –
 * there is no structured `issued` field on the consumer side (see #1618). To
 * sort entries within a year by what the reader actually sees, we parse the
 * date out of that citation text.
 *
 * Handling differs by citation style:
 * - APA 7th: the full date is the first parenthesised group after the authors,
 *   e.g. `(2025, November 18)`, `(2019, April)`, `(2020)`.
 * - Harvard: only the year is parenthesised; day and month follow later in the
 *   entry, e.g. `(2025) ‘Title’. Event, Basel, 21 October.`
 * - IEEE: date sits at the end of the entry, month abbreviated,
 *   e.g. `… Franz Steiner, Aug. 2015.` (detected via the `csl-left-margin`
 *   marker that also drives the `[n]` numbering).
 *
 * Entries usually end in a locator tail (`doi: 10.1088/2058-9565/…`,
 * `Available at: https://…`) whose digit groups match a year regex — that tail
 * is cut off before scanning so a DOI never wins over the publication date.
 *
 * Missing parts are returned as `0` so callers can treat a bare year as "later
 * sorts last within the year" and undated entries (`year === 0`) as "sort to the
 * end".
 */

export interface CitationDate {
  /** Four digit year, or 0 when no date could be parsed. */
  year: number;
  /** 1–12, or 0 when the month is absent. */
  month: number;
  /** 1–31, or 0 when the day is absent. */
  day: number;
}

const EMPTY: CitationDate = { year: 0, month: 0, day: 0 };

// English full + abbreviated (IEEE) + German (defensive, in case a CSL locale
// ever renders month names localised).
const MONTHS: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  sept: 9,
  oct: 10,
  nov: 11,
  dec: 12,
  januar: 1,
  februar: 2,
  märz: 3,
  maerz: 3,
  mai: 5,
  juni: 6,
  juli: 7,
  oktober: 10,
  dezember: 12,
};

const YEAR_RE = /\b(?:19|20)\d{2}\b/;
const YEAR_RE_G = /\b(?:19|20)\d{2}\b/g;

const monthToNumber = (name: string): number =>
  MONTHS[name.toLowerCase().replace(/\.$/, '')] ?? 0;

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;|&#\d+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Cuts off the locator tail (`doi: …`, `Available at: https://…`) so its digit
 * groups (`10.1088/2058-9565/…`, `…jaerosci.2018.11.011`) are never mistaken
 * for the publication date.
 */
const truncateLocatorTail = (text: string): string => {
  const index = text.search(/\bdoi:|\bavailable(?:\s+at)?:|\bhttps?:\/\//i);
  return index === -1 ? text : text.slice(0, index);
};

/**
 * Finds a month with an adjacent day number: `21 October` (Harvard) or
 * `November 18` (APA). Requiring the day keeps a bare month word inside a
 * title (e.g. “… may …”) from being read as a date.
 */
const parseAdjacentDayAndMonth = (
  text: string,
): { month: number; day: number } => {
  for (const match of text.matchAll(/\b(\d{1,2})\.?\s+([A-Za-zäöüÄÖÜ]{3,})/g)) {
    const month = monthToNumber(match[2]);
    if (month) return { month, day: parseInt(match[1], 10) };
  }
  for (const match of text.matchAll(/([A-Za-zäöüÄÖÜ]{3,})\.?\s+(\d{1,2})\b/g)) {
    const month = monthToNumber(match[1]);
    if (month) return { month, day: parseInt(match[2], 10) };
  }
  return { month: 0, day: 0 };
};

/**
 * APA / Harvard: the year is in the first parenthesised group that contains
 * one. APA renders month and day inside that group, Harvard after it.
 */
const parseParenthesisedDate = (text: string): CitationDate => {
  const groups = text.match(/\(([^)]*)\)/g) || [];
  for (const group of groups) {
    const inner = group.slice(1, -1);
    const yearMatch = inner.match(YEAR_RE);
    if (!yearMatch) continue;

    const year = parseInt(yearMatch[0], 10);
    // Everything after the year, e.g. ", November 18" or ", April".
    const rest = inner.slice(inner.indexOf(yearMatch[0]) + 4);
    let { month, day } = parseAdjacentDayAndMonth(rest);
    if (month === 0) {
      // Month without a day, e.g. `(2019, April)`.
      const monthMatch = rest.match(/[A-Za-zäöüÄÖÜ]{3,}/);
      month = monthMatch ? monthToNumber(monthMatch[0]) : 0;
    }
    if (month === 0) {
      // Harvard: day and month follow after the closing parenthesis,
      // e.g. `(2025) ‘Title’. Event, Basel, 21 October.`
      const after = text.slice(text.indexOf(group) + group.length);
      ({ month, day } = parseAdjacentDayAndMonth(truncateLocatorTail(after)));
    }

    return { year, month, day };
  }
  return EMPTY;
};

/**
 * IEEE: the publication date is the last year in the entry (locator tail
 * excluded), optionally preceded by an abbreviated month.
 */
const parseIeeeDate = (fullText: string): CitationDate => {
  const text = truncateLocatorTail(fullText);
  const years = [...text.matchAll(YEAR_RE_G)];
  if (years.length === 0) return EMPTY;

  const last = years[years.length - 1];
  const year = parseInt(last[0], 10);
  const before = text.slice(Math.max(0, (last.index ?? 0) - 20), last.index);
  const monthMatch = before.match(/([A-Za-z]{3,9})\.?\s*$/);
  const month = monthMatch ? monthToNumber(monthMatch[1]) : 0;

  return { year, month, day: 0 };
};

/**
 * Parses a sortable date out of a rendered citation entry.
 * Returns `{ year: 0, month: 0, day: 0 }` when no date is found (e.g. project
 * entries that use a different HTML layout).
 */
export const parseCitationDate = (citationEntry?: string): CitationDate => {
  if (!citationEntry) return EMPTY;

  const isIeee = citationEntry.includes('csl-left-margin');
  const text = stripHtml(citationEntry);

  return isIeee ? parseIeeeDate(text) : parseParenthesisedDate(text);
};
